/**
 * Fixation Holes Editor — 2D "back-view" modal to interactively place drilling
 * marks on each segment. Coordinates are stored in real mm (x from left edge,
 * y from bottom of the letter envelope, per segment).
 *
 * Interactions:
 *   - Click on empty area over a segment → adds a new Ø6 mm hole at that point
 *   - Click on an existing hole marker  → deletes it
 *   - Change the Ø field of a hole in the side panel to override its diameter
 *   - "Reset" button clears all custom holes of a segment (falls back to auto)
 *   - "Save & close" writes to the store
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, RotateCcw, Save, Info } from 'lucide-react';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import useEditorStore from '../store/useEditorStore';

const FONT_URL = '/fonts/helvetiker_bold.typeface.json';
let _fontPromise = null;
function getFont() {
  if (!_fontPromise) {
    _fontPromise = new Promise((resolve, reject) => {
      const loader = new FontLoader();
      loader.load(FONT_URL, resolve, undefined, reject);
    });
  }
  return _fontPromise;
}

function uid() { return Math.random().toString(36).slice(2, 9); }

function segmentShapes(font, text, heightMm) {
  const safeText = typeof text === 'string' && text.length > 0 ? text : ' ';
  try {
    return font.generateShapes(safeText, heightMm) || [];
  } catch {
    return [];
  }
}

function segmentWidthMm(shapes, fallbackW) {
  let minX = Infinity, maxX = -Infinity;
  shapes.forEach((s) => {
    try {
      s.getPoints(14).forEach((p) => {
        if (!p || !Number.isFinite(p.x)) return;
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
      });
    } catch { /* ignore */ }
  });
  if (Number.isFinite(maxX - minX) && maxX > minX) return maxX - minX;
  return fallbackW;
}

// Convert a Three.js Shape to an SVG path string (font Y up → SVG Y down).
function shapeToSvgPath(shape) {
  if (!shape || typeof shape.getPoints !== 'function') return '';
  const outer = shape.getPoints(28);
  if (!outer || outer.length < 2) return '';
  const parts = [];
  const drawRing = (pts) => {
    if (!pts || pts.length < 2) return;
    parts.push(`M ${pts[0].x.toFixed(2)} ${(-pts[0].y).toFixed(2)}`);
    for (let i = 1; i < pts.length; i++) {
      parts.push(`L ${pts[i].x.toFixed(2)} ${(-pts[i].y).toFixed(2)}`);
    }
    parts.push('Z');
  };
  drawRing(outer);
  (Array.isArray(shape.holes) ? shape.holes : []).forEach((h) => {
    if (h && typeof h.getPoints === 'function') drawRing(h.getPoints(28));
  });
  return parts.join(' ');
}

export default function FixationHolesEditor({ open, onClose }) {
  const segments = useEditorStore((s) => s.segments);
  const dimensions = useEditorStore((s) => s.dimensions);
  const setSegmentCustomHoles = useEditorStore((s) => s.setSegmentCustomHoles);
  const clearSegmentCustomHoles = useEditorStore((s) => s.clearSegmentCustomHoles);

  const [font, setFont] = useState(null);
  const [error, setError] = useState('');
  const [layout, setLayout] = useState([]);   // per-segment measured layout
  const [holes, setHoles] = useState({});      // segId -> [{ id, xMm, yMm, diameterMm }]
  const svgRef = useRef(null);

  const heightMm = Number.isFinite(dimensions?.height) ? dimensions.height : 150;

  // Load font once on open
  useEffect(() => {
    if (!open) return;
    setError('');
    getFont().then(setFont).catch((e) => setError(String(e?.message || e)));
  }, [open]);

  // Snapshot store holes into local state when opened
  useEffect(() => {
    if (!open) return;
    const init = {};
    segments.forEach((seg) => {
      init[seg.id] = Array.isArray(seg.customHoles)
        ? seg.customHoles.map((h) => ({
            id: h.id || uid(),
            xMm: Number(h.xMm) || 0,
            yMm: Number(h.yMm) || 0,
            diameterMm: Number(h.diameterMm) || 6,
          }))
        : [];
    });
    setHoles(init);
  }, [open, segments]);

  // Compute mm-layout of all segments (side by side, like the plan de pose)
  useEffect(() => {
    if (!open || !font) return;
    const gapDefault = 15;
    let cursor = 0;
    const arr = segments.map((seg) => {
      const shapes = segmentShapes(font, seg.text || ' ', heightMm);
      const width = segmentWidthMm(shapes, Math.max(1, heightMm * 0.6 * (seg.text || '').length));
      const x = cursor;
      const gap = seg.detached ? Number(seg.gapAfterMm || 30) : gapDefault;
      cursor += width + gap;
      return { seg, shapes, width, x };
    });
    setLayout(arr);
  }, [open, font, segments, heightMm]);

  const totalWmm = useMemo(() => layout.reduce((acc, sd, i) => {
    const gap = sd.seg?.detached ? Number(sd.seg.gapAfterMm || 30) : 15;
    return acc + sd.width + (i < layout.length - 1 ? gap : 0);
  }, 0), [layout]);

  // Fit-to-viewport scale factor (px per mm)
  const viewportPx = { w: 1100, h: 380 };
  const padPx = 40;
  const pxPerMm = useMemo(() => {
    if (totalWmm <= 0) return 1;
    const sx = (viewportPx.w - padPx * 2) / totalWmm;
    const sy = (viewportPx.h - padPx * 2) / heightMm;
    return Math.min(sx, sy);
  }, [totalWmm, heightMm]);

  const svgW = viewportPx.w;
  const svgH = viewportPx.h;

  // World → screen helpers
  const worldToScreen = (segX, xMm, yMm) => ({
    x: padPx + (segX + xMm) * pxPerMm,
    y: (svgH - padPx) - yMm * pxPerMm,
  });
  const screenToSegXY = (sx, sy, sd) => {
    const xMmWorld = (sx - padPx) / pxPerMm;
    const yMm = ((svgH - padPx) - sy) / pxPerMm;
    return { xMm: xMmWorld - sd.x, yMm };
  };

  // Handlers — convert client px to SVG viewBox units so the hit-test is robust
  // regardless of how the SVG is stretched by CSS (`width="100%"`).
  const onSvgClick = (e) => {
    if (!svgRef.current || !layout.length) return;
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = svgW / (rect.width || svgW);
    const scaleY = svgH / (rect.height || svgH);
    const sx = (e.clientX - rect.left) * scaleX;
    const sy = (e.clientY - rect.top) * scaleY;
    // Locate which segment we hit (generous 16-unit tolerance in viewBox space)
    for (const sd of layout) {
      const segLeftPx = padPx + sd.x * pxPerMm;
      const segRightPx = padPx + (sd.x + sd.width) * pxPerMm;
      const segTopPx = (svgH - padPx) - heightMm * pxPerMm;
      const segBotPx = (svgH - padPx);
      if (sx >= segLeftPx - 16 && sx <= segRightPx + 16 && sy >= segTopPx - 16 && sy <= segBotPx + 16) {
        const { xMm, yMm } = screenToSegXY(sx, sy, sd);
        // Clamp inside segment
        const cx = Math.max(0, Math.min(sd.width, xMm));
        const cy = Math.max(0, Math.min(heightMm, yMm));
        setHoles((prev) => ({
          ...prev,
          [sd.seg.id]: [...(prev[sd.seg.id] || []), { id: uid(), xMm: cx, yMm: cy, diameterMm: 6 }],
        }));
        return;
      }
    }
  };

  const deleteHole = (segId, holeId, ev) => {
    ev?.stopPropagation?.();
    setHoles((prev) => ({
      ...prev,
      [segId]: (prev[segId] || []).filter((h) => h.id !== holeId),
    }));
  };

  const setHoleDiameter = (segId, holeId, d) => {
    const v = Math.max(1, Math.min(50, Number(d) || 6));
    setHoles((prev) => ({
      ...prev,
      [segId]: (prev[segId] || []).map((h) => h.id === holeId ? { ...h, diameterMm: v } : h),
    }));
  };

  const resetSegment = (segId) => {
    setHoles((prev) => ({ ...prev, [segId]: [] }));
  };

  const saveAndClose = () => {
    // Write back to store
    Object.keys(holes).forEach((segId) => {
      const list = holes[segId] || [];
      if (list.length === 0) {
        clearSegmentCustomHoles(segId);
      } else {
        setSegmentCustomHoles(segId, list);
      }
    });
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fix-holes-editor">
      <div className="bg-[#1e1e26] text-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Placement des trous de fixation</h2>
            <p className="text-xs text-white/60 mt-0.5 flex items-center gap-1.5">
              <Info size={12} /> Vue arrière · cliquez sur le dos d&apos;un élément pour ajouter un trou · cliquez sur un trou existant pour le supprimer
            </p>
          </div>
          <button
            className="p-2 rounded-lg hover:bg-white/10 transition"
            onClick={onClose}
            data-testid="fix-holes-close"
            title="Fermer sans sauvegarder"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5">
          {error && (
            <div className="mb-3 p-3 rounded-lg bg-red-500/20 border border-red-500/40 text-red-200 text-sm">
              Erreur police : {error}
            </div>
          )}

          {/* SVG canvas */}
          <div className="rounded-xl bg-white overflow-hidden border border-white/10">
            <svg
              ref={svgRef}
              width="100%"
              viewBox={`0 0 ${svgW} ${svgH}`}
              onClick={onSvgClick}
              style={{ cursor: 'crosshair', display: 'block' }}
              data-testid="fix-holes-svg"
            >
              {/* Envelope background */}
              <rect x={padPx - 6} y={(svgH - padPx) - heightMm * pxPerMm - 6}
                width={totalWmm * pxPerMm + 12} height={heightMm * pxPerMm + 12}
                fill="#fafafa" stroke="#c9c9d1" strokeDasharray="4 4" />

              {/* Segments (letter outlines) */}
              {layout.map((sd) => {
                const segLeftPx = padPx + sd.x * pxPerMm;
                const segBotPx = svgH - padPx;
                return (
                  <g key={sd.seg.id}>
                    {/* Element frame */}
                    <rect
                      x={segLeftPx}
                      y={segBotPx - heightMm * pxPerMm}
                      width={sd.width * pxPerMm}
                      height={heightMm * pxPerMm}
                      fill="#f4f4f6"
                      stroke="#a0a0aa"
                      strokeWidth={0.6}
                    />
                    {/* Letter outlines (font path in mm) — transform to place at (segLeftPx, segBotPx) with scale pxPerMm */}
                    <g transform={`translate(${segLeftPx}, ${segBotPx}) scale(${pxPerMm}, ${pxPerMm})`}>
                      {sd.shapes.map((shape, i) => (
                        <path
                          key={i}
                          d={shapeToSvgPath(shape)}
                          fill="#e5e5ec"
                          stroke="#333"
                          strokeWidth={0.4 / pxPerMm}
                          fillRule="evenodd"
                        />
                      ))}
                    </g>
                    {/* Segment label */}
                    <text
                      x={segLeftPx + (sd.width * pxPerMm) / 2}
                      y={segBotPx - heightMm * pxPerMm - 8}
                      textAnchor="middle"
                      fontSize={11}
                      fill="#666"
                    >
                      {sd.seg.text || 'Segment'} · {Math.round(sd.width)}×{Math.round(heightMm)} mm
                    </text>
                    {/* Symmetry axis */}
                    <line
                      x1={segLeftPx + (sd.width * pxPerMm) / 2}
                      y1={segBotPx - heightMm * pxPerMm - 4}
                      x2={segLeftPx + (sd.width * pxPerMm) / 2}
                      y2={segBotPx + 4}
                      stroke="#c9c9d1"
                      strokeWidth={0.5}
                      strokeDasharray="2 2"
                    />
                  </g>
                );
              })}

              {/* Holes */}
              {layout.map((sd) => (holes[sd.seg.id] || []).map((h) => {
                const p = worldToScreen(sd.x, h.xMm, h.yMm);
                const r = Math.max(4, (h.diameterMm / 2) * pxPerMm);
                return (
                  <g key={h.id} onClick={(e) => deleteHole(sd.seg.id, h.id, e)} style={{ cursor: 'pointer' }}>
                    <circle cx={p.x} cy={p.y} r={r} fill="#dc2670" stroke="#fff" strokeWidth={1.4} />
                    <line x1={p.x - r * 2} y1={p.y} x2={p.x + r * 2} y2={p.y} stroke="#dc2670" strokeWidth={1.2} />
                    <line x1={p.x} y1={p.y - r * 2} x2={p.x} y2={p.y + r * 2} stroke="#dc2670" strokeWidth={1.2} />
                    <text x={p.x + r * 2 + 4} y={p.y - r - 2} fill="#dc2670" fontSize="10" fontWeight="700">
                      Ø{h.diameterMm}
                    </text>
                  </g>
                );
              }))}
            </svg>
          </div>

          {/* Segment list — per-hole editors */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {layout.map((sd) => {
              const list = holes[sd.seg.id] || [];
              return (
                <div key={sd.seg.id} className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-sm">
                      {sd.seg.text || 'Segment'} <span className="text-white/50 text-xs">· {list.length} trou{list.length > 1 ? 's' : ''}</span>
                    </div>
                    <button
                      className="text-xs px-2 py-1 rounded hover:bg-white/10 text-white/70 flex items-center gap-1"
                      onClick={() => resetSegment(sd.seg.id)}
                      data-testid={`fix-holes-reset-${sd.seg.id}`}
                      title="Effacer tous les trous de ce segment (retour à l'auto)"
                    >
                      <RotateCcw size={12} /> Reset
                    </button>
                  </div>
                  {list.length === 0 ? (
                    <div className="text-xs text-white/50 italic">Aucun trou personnalisé — les trous auto s&apos;appliqueront.</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {list.map((h, i) => (
                        <li key={h.id} className="flex items-center gap-2 text-xs">
                          <span className="text-white/70 w-8">P{i + 1}</span>
                          <span className="text-white/60">x={Math.round(h.xMm)} · y={Math.round(h.yMm)} mm</span>
                          <span className="ml-auto flex items-center gap-1">
                            Ø
                            <input
                              type="number"
                              min="1"
                              max="50"
                              step="0.5"
                              value={h.diameterMm}
                              onChange={(e) => setHoleDiameter(sd.seg.id, h.id, e.target.value)}
                              className="w-14 bg-black/30 border border-white/10 rounded px-1.5 py-0.5 text-right"
                              data-testid={`fix-holes-dia-${h.id}`}
                            />
                            mm
                          </span>
                          <button
                            className="text-red-300 hover:text-red-100 px-1"
                            onClick={() => deleteHole(sd.seg.id, h.id)}
                            data-testid={`fix-holes-del-${h.id}`}
                            title="Supprimer ce trou"
                          >
                            <X size={12} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between gap-3 bg-black/20">
          <div className="text-xs text-white/60">
            Les trous personnalisés <b>remplacent</b> l&apos;équilibrage automatique sur le plan de pose.
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 rounded-lg text-sm bg-white/10 hover:bg-white/15 transition"
              onClick={onClose}
              data-testid="fix-holes-cancel"
            >
              Annuler
            </button>
            <button
              className="px-4 py-1.5 rounded-lg text-sm bg-[#ec1d8c] hover:bg-[#ff2d9c] transition flex items-center gap-1.5 font-medium"
              onClick={saveAndClose}
              data-testid="fix-holes-save"
            >
              <Save size={14} /> Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
